/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { color } from '@ahamove/tokens';
import { cn } from '@ahm/common-helpers';
import { ActionIcon } from '@mantine/core';
import React, { forwardRef, useEffect, useState } from 'react';
import XCircle from '@/assets/icons/xcircle-icon.svg';

export interface AhaNumberInputProps {
  label?: string;
  placeholder?: string;
  value?: number | string;
  defaultValue?: number | string;
  onChange?: (value: number | string) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  error?: string | boolean;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  thousandSeparator?: string;
  decimalSeparator?: string;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  id?: string;
  name?: string;
  size?: 'sm' | 'md';
}

/** ---------- Helpers ---------- */
const addThousandSeparators = (numStr: string, sep: string) =>
  numStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

const formatNumber = (
  num: number | string,
  thousandSeparator: string,
  decimalSeparator: string
): string => {
  if (num === '' || num === undefined || num === null) return '';
  const str = String(num);
  const [intPart, decPart = ''] = str.replace('-', '').split('.');
  const formattedInt = addThousandSeparators(intPart, thousandSeparator);
  const formatted =
    decPart || str.includes('.')
      ? `${formattedInt}${decimalSeparator}${decPart}`
      : formattedInt;
  return str.startsWith('-') ? `-${formatted}` : formatted;
};

const parseNumber = (
  formatted: string,
  thousandSeparator: string,
  decimalSeparator: string
): number | string => {
  if (formatted === '' || formatted === '-') return '';
  const cleaned = formatted
    .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
    .replace(decimalSeparator, '.');
  if (cleaned === '-') return '-';
  return isNaN(Number(cleaned)) ? '' : Number(cleaned);
};

const isAllowedKey = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
  const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
  const ctrlKeys = ['a', 'c', 'v', 'x', 'z'];
  const navKeys = [
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
  ];
  return (
    allowed.includes(e.key) ||
    (ctrlKeys.includes(e.key.toLowerCase()) && e.ctrlKey) ||
    navKeys.includes(e.key)
  );
};

const isValidNumberKey = (
  e: React.KeyboardEvent<HTMLInputElement>,
  decimalSeparator: string
): boolean => {
  const isNumber = /^\d$/.test(e.key);
  return (
    isNumber || e.key === decimalSeparator || e.key === '-' || e.key === '.'
  );
};

/** ---------- Sub Components ---------- */
const getLabelColor = (error: boolean, disabled: boolean, focused: boolean) => {
  if (error && !disabled) return color.red[50];
  if (focused && !error) return color.neutral[60];
  if (disabled) return color.neutral[40];
  return color.neutral[60];
};

const getLabelPosition = (
  floating: boolean,
  size: string,
  leftSection: boolean
) => {
  const baseClasses = floating
    ? size === 'sm'
      ? 'left-3.5 top-1 text-xs font-semibold'
      : 'left-3.5 top-2 text-xs font-semibold'
    : size === 'sm'
      ? 'left-3.5 top-2'
      : 'left-3.5 top-4';

  const leftOffset = leftSection && (size === 'sm' ? 'left-11' : 'left-12');
  return cn(baseClasses, leftOffset);
};

const Label = ({
  htmlFor,
  label,
  required,
  floating,
  size,
  leftSection,
  labelClassName,
  disabled,
  focused,
  error,
}: any) => {
  if (!label) return null;
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'pointer-events-none absolute z-10 font-medium transition-all duration-100 ease-out',
        getLabelPosition(floating, size, leftSection),
        labelClassName
      )}
      style={{
        color: getLabelColor(error, disabled, focused),
      }}
    >
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
};

const LeftSection = ({ leftSection, size }: any) =>
  leftSection ? (
    <div
      className={cn(
        'text-gray-500 pointer-events-none absolute top-1/2 z-10 -translate-y-1/2',
        size === 'sm' ? 'left-3' : 'left-3.5'
      )}
    >
      {leftSection}
    </div>
  ) : null;

const RightSection = ({
  rightSection,
  clearable,
  hasValue,
  disabled,
  size,
  onClear,
}: any) => {
  if (!rightSection && !(clearable && hasValue && !disabled)) return null;
  return (
    <div
      className={cn(
        'absolute top-[30%] z-10 -translate-y-1/2',
        size === 'sm' ? 'right-3' : 'right-3.5'
      )}
    >
      {rightSection ||
        (clearable && hasValue && (
          <ActionIcon
            aria-label="Clear input"
            variant="transparent"
            onClick={onClear}
          >
            <XCircle />
          </ActionIcon>
        ))}
    </div>
  );
};

const ErrorMessage = ({ error }: { error?: string | boolean }) =>
  error && typeof error === 'string' ? (
    <p className="mb-0 mt-1 text-xs leading-4" style={{ color: color.red[60] }}>
      {error}
    </p>
  ) : null;

/** ---------- Component ---------- */
const AhaNumberInput = forwardRef<HTMLInputElement, AhaNumberInputProps>(
  (
    {
      label,
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      leftSection,
      rightSection,
      clearable = false,
      onClear,
      disabled = false,
      error = false,
      required = false,
      min,
      max,
      step,
      precision: _precision = 2,
      thousandSeparator = ',',
      decimalSeparator = '.',
      className,
      inputClassName,
      labelClassName,
      id,
      name,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const [rawValue, setRawValue] = useState<number | string>('');
    const [focused, setFocused] = useState(false);

    /** Initialize value */
    useEffect(() => {
      const initial = value ?? defaultValue ?? '';
      setRawValue(initial);
      setInternalValue(
        formatNumber(initial, thousandSeparator, decimalSeparator)
      );
    }, [value, defaultValue, thousandSeparator, decimalSeparator]);

    /** Sync external value */
    useEffect(() => {
      if (value !== undefined && value !== rawValue && !focused) {
        setRawValue(value);
        setInternalValue(
          formatNumber(value, thousandSeparator, decimalSeparator)
        );
      }
    }, [value, rawValue, focused, thousandSeparator, decimalSeparator]);

    /** Handlers */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
      const parsed = parseNumber(
        e.target.value,
        thousandSeparator,
        decimalSeparator
      );
      setRawValue(parsed);
      onChange?.(parsed);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (rawValue !== '') {
        setInternalValue(
          formatNumber(rawValue, thousandSeparator, decimalSeparator)
        );
      }
      onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        !isAllowedKey(e) &&
        (!isValidNumberKey(e, decimalSeparator) || e.shiftKey)
      ) {
        e.preventDefault();
      }
    };

    const handleClear = () => {
      setInternalValue('');
      setRawValue('');
      onChange?.('');
      onClear?.();
    };

    /** UI states */
    const hasValue = internalValue !== '';
    const floating = focused || hasValue;
    const inputId =
      id || `aha-input-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={cn('relative', className)}>
        <div className="relative">
          <LeftSection leftSection={leftSection} size={size} />
          <Label
            htmlFor={inputId}
            label={label}
            required={required}
            floating={floating}
            size={size}
            leftSection={leftSection}
            labelClassName={labelClassName}
            disabled={disabled}
            focused={focused}
            error={error}
          />
          <input
            ref={ref}
            id={inputId}
            name={name}
            type="text"
            value={internalValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={focused ? placeholder : ''}
            min={min}
            max={max}
            step={step}
            required={required}
            style={{
              borderColor:
                focused && !error
                  ? color.primary[50]
                  : error
                    ? color.red[50]
                    : color.neutral[30],
            }}
            className={cn(
              'w-full rounded-md border border-solid bg-white transition-all duration-200 ease-in-out',
              'text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap font-medium',
              'focus:outline-none',
              size === 'sm'
                ? 'h-12 pb-1 pt-5 text-base'
                : 'h-14 pb-2 pt-6 text-lg',
              leftSection ? 'pl-12' : 'pl-3.5',
              rightSection ? 'pr-12' : 'pr-3.5',
              disabled && 'bg-gray-50 text-gray-400 cursor-not-allowed',
              'placeholder:opacity-0',
              focused && 'placeholder:text-gray-400 placeholder:opacity-100',
              inputClassName
            )}
            {...props}
          />
          <RightSection
            rightSection={rightSection}
            clearable={clearable}
            hasValue={hasValue}
            disabled={disabled}
            size={size}
            onClear={handleClear}
          />
        </div>
        <ErrorMessage error={error} />
      </div>
    );
  }
);

AhaNumberInput.displayName = 'AhaNumberInput';
export default AhaNumberInput;
